import React, { useEffect, useRef, useState } from 'react';
import { AutoComplete, Button, Form, Input, Table, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { searchListObjects, resolveListObjects } from '../../../../services/cms/contentManagementService';

const { Text } = Typography;

// Bám form Danh sách của lichviet_cms (backend/list/form.blade.php):
// ô tìm kiếm gợi ý theo loại item đang chọn → bảng Tên | Thứ tự | Xóa.
// value = [{ id, title, order }]; title có thể thiếu khi mở form sửa → tự tra tên qua API.
const ListItemsPicker = ({ value, onChange }) => {
  const items = Array.isArray(value) ? value : [];
  const form = Form.useFormInstance();
  const itemType = Form.useWatch('item_type', form);
  const prevTypeRef = useRef(itemType);
  const [keyword, setKeyword] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(keyword.trim()), 300);
    return () => clearTimeout(t);
  }, [keyword]);

  // CMS cũ: đổi loại item thì resetObject() — xoá danh sách đã chọn vì id thuộc bảng khác
  useEffect(() => {
    if (prevTypeRef.current !== undefined && itemType !== undefined && prevTypeRef.current !== itemType && items.length > 0) {
      onChange([]);
    }
    prevTypeRef.current = itemType;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemType]);

  const { data: suggestions = [] } = useQuery({
    queryKey: ['listSearchObjects', itemType, debounced],
    queryFn: () => searchListObjects({ search: debounced, item_type: itemType }),
    enabled: !!itemType && debounced.length > 0,
  });

  const missingIds = items.filter((it) => it.title === undefined || it.title === null).map((it) => it.id);
  const { data: resolved } = useQuery({
    queryKey: ['listResolveObjects', itemType, missingIds.join(',')],
    queryFn: () => resolveListObjects({ ids: missingIds, item_type: itemType }),
    enabled: !!itemType && missingIds.length > 0,
  });
  useEffect(() => {
    if (!resolved || resolved.length === 0) return;
    const byId = new Map(resolved.map((r) => [Number(r.id), r.title]));
    onChange(items.map((it) => (it.title === undefined || it.title === null) && byId.has(Number(it.id)) ? { ...it, title: byId.get(Number(it.id)) } : it));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved]);

  const handleSelect = (_, option) => {
    const id = Number(option.key);
    if (items.some((it) => Number(it.id) === id)) {
      setKeyword('');
      return;
    }
    onChange([...items, { id, title: option.title, order: items.length + 1 }]);
    setKeyword('');
  };

  const updateOrder = (id, order) => onChange(items.map((it) => (it.id === id ? { ...it, order } : it)));
  const removeItem = (id) => onChange(items.filter((it) => it.id !== id));

  const columns = [
    { title: 'Tên', dataIndex: 'title', key: 'title', render: (t, r) => t ?? <Text type="secondary">#{r.id} …</Text> },
    {
      title: 'Thứ tự',
      dataIndex: 'order',
      key: 'order',
      width: 110,
      render: (o, r) => <Input type="number" value={o} onChange={(e) => updateOrder(r.id, e.target.value === '' ? '' : Number(e.target.value))} />,
    },
    {
      title: '',
      key: 'action',
      width: 80,
      render: (_, r) => (
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeItem(r.id)}>
          Xóa
        </Button>
      ),
    },
  ];

  return (
    <div>
      <AutoComplete
        className="w-full"
        value={keyword}
        onChange={setKeyword}
        onSelect={handleSelect}
        disabled={!itemType}
        placeholder={itemType ? 'Tìm kiếm ...' : 'Chọn loại item trước'}
        options={suggestions.map((s) => ({ key: String(s.id), value: `${s.id}`, label: `${s.title} (#${s.id})`, title: s.title }))}
      />
      <Table className="mt-3" size="small" rowKey="id" pagination={false} columns={columns} dataSource={items} locale={{ emptyText: 'Chưa chọn item nào' }} />
    </div>
  );
};

export default ListItemsPicker;
